import React, { useMemo, useState, useEffect } from 'react';
import groupRowStyles from './GroupRow.module.scss';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import SelectMembersDialog from './SelectMembersDialog';
import GroupRow from './GroupRow';
import ListContent from '../composites/ListContent';
import EditGroup from './EditGroup';
import GroupMembersView from './GroupMembersView';
import InDepthMember from '../Clients/InDepthMember';
import { isHighSchool as _isHighSchool, getFullName } from '../../utils';
import { useSessionData, useUserAction } from '../../providers';

import EditProgram from '../Programs/EditProgram';

import type {
  Group,
  WithOptionalId,
  AssignedProgram,
  Member,
  Program,
} from '../../types';
import { UserType } from '../../constants';
import _ from 'lodash';

const groupListTitles = [
  'Group Name',
  'Members',
  'Created By',
  'Program',
  'Options',
];

const getEmptyGroup = (trainerId: string) => ({
  name: '',
  memberIds: [],
  managerIds: [],
  trainerIds: [trainerId],
});

const Groups: React.FC = () => {
  const {
    loginTrainer,
    gym,
    groups,
    trainers: allGymTrainers,
    members: allGymMembers,
  } = useSessionData();
  const {
    createGroup,
    updateGroup,
    deleteGroup,
    createProgram,
    updateProgramAndRefreshPrograms,
    assignProgramAndRefreshMembers,
  } = useUserAction();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedGroupForDrillDown, setSelectedGroupForDrillDown] =
    useState<Group | null>(null);
  const [selectedMemberForDrillDown, setSelectedMemberForDrillDown] =
    useState<Member | null>(null);
  const [editedGroup, setEditedGroup] = useState<WithOptionalId<Group> | null>(
    null,
  );
  const [editedProgram, setEditedProgram] =
    useState<WithOptionalId<AssignedProgram> | null>(null);
  const [isProgramsDrillInOpen, setIsProgramsDrillInOpen] = useState(false);
  const [isEditGroupDrillInOpen, setIsEditGroupDrillInOpen] = useState(false);
  const [isSelectMembersModalOpen, setIsSelectMembersModalOpen] =
    useState(false);

  const isHighSchool = gym ? _isHighSchool(gym) : false;
  const userType: UserType = isHighSchool ? UserType.Athlete : UserType.Client;

  const filteredGroups = useMemo(() => {
    if (!Object.values(groups).length) return [];
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return Object.values(groups)
      .map((group) => ({
        ...group,
        memberIds: group.memberIds.filter(
          (memberId) =>
            Object.values(allGymMembers).find(
              (member) => member.id === memberId,
            )?.isActive,
        ),
      }))
      .filter(({ name }) => {
        if (!normalizedQuery) return true;
        return name.toLowerCase().includes(normalizedQuery);
      })
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [searchQuery, groups, allGymMembers]);

  // Update selectedGroupForDrillDown when groups data changes
  useEffect(() => {
    if (selectedGroupForDrillDown && groups) {
      const updatedGroup = Object.values(groups).find(
        (group) => group.id === selectedGroupForDrillDown.id,
      );
      if (updatedGroup) {
        setSelectedGroupForDrillDown(updatedGroup);
      }
    }
  }, [groups, selectedGroupForDrillDown]);

  // Keep selected member drill-down in sync with freshest member store data.
  useEffect(() => {
    if (!selectedMemberForDrillDown) {
      return;
    }
    const latestMember = allGymMembers[selectedMemberForDrillDown.id];
    if (!latestMember) {
      setSelectedMemberForDrillDown(null);
      return;
    }
    if (!_.isEqual(latestMember, selectedMemberForDrillDown)) {
      setSelectedMemberForDrillDown(latestMember);
    }
  }, [allGymMembers, selectedMemberForDrillDown]);

  // console.log('filteredGroups', filteredGroups);

  if (!loginTrainer) {
    return (
      <EmptyState
        icon={<GroupsOutlinedIcon />}
        text="No Groups for logged in role."
      />
    );
  }

  const renderEmptyState = () => (
    <EmptyState
      icon={<GroupsOutlinedIcon />}
      cta={{
        icon: <AddBoxOutlinedIcon color="primary" />,
        label: 'Add A Group',
        onClick: () =>
          handleOpenEditGroupDrillIn(getEmptyGroup(loginTrainer.id)),
      }}
      text="No Groups Have Been Added"
    />
  );

  const removeGroup = async (group: Group) => {
    await deleteGroup(group);
  };

  const handleEditGroup = async (
    currentGroupData: WithOptionalId<Group>,
    newGroupData: WithOptionalId<Group>,
  ): Promise<boolean> => {
    const success = newGroupData.id
      ? await updateGroup(currentGroupData as Group, newGroupData as Group)
      : await createGroup(
          newGroupData,
          [loginTrainer.id],
          newGroupData.memberIds,
        );
    return success;
  };

  const assignProgramToSelectedMembers = async (memberIds: string[]) => {
    if (!editedProgram || !editedGroup) {
      setIsSelectMembersModalOpen(false);
      setEditedProgram(null);
      setEditedGroup(null);
      return;
    }
    const updatedProgram = {
      ...editedProgram,
      startDate: editedProgram.startDate || new Date().toISOString(),
    };
    const updatedGroup = {
      ...editedGroup,
      program: updatedProgram as AssignedProgram,
    };
    await Promise.all([
      assignProgramAndRefreshMembers(
        memberIds,
        updatedProgram as AssignedProgram,
      ),
      handleEditGroup(editedGroup as Group, updatedGroup as Group),
    ]);
    setIsSelectMembersModalOpen(false);
    setEditedProgram(null);
    setEditedGroup(null);
  };

  const handleCloseSelectMembersDialog = () => {
    setEditedProgram(null);
    setEditedGroup(null);
    setIsSelectMembersModalOpen(false);
  };

  const renderSelectMembersDialog = () => (
    <SelectMembersDialog
      onSubmit={assignProgramToSelectedMembers}
      isOpen={isSelectMembersModalOpen}
      onClose={handleCloseSelectMembersDialog}
      memberIds={editedGroup?.memberIds || []}
    />
  );

  const handleEditGroupProgram = async (group: Group) => {
    const currentProgram = group?.program;
    if (!currentProgram?.id) {
      return;
    } else {
      setEditedGroup(group);
      setEditedProgram(currentProgram);
      setIsProgramsDrillInOpen(true);
    }
  };

  const handleCreateGroupProgram = async (group: Group) => {
    setEditedGroup(group);
    setEditedProgram({
      id: undefined,
      name: '',
      trainerId: loginTrainer.id,
      level: 0,
      fitnessGoal: '',
      programItems: [],
      isPrivate: true,
      startDate: new Date().toISOString(),
    });
    setIsProgramsDrillInOpen(true);
  };

  const handleSubmitEditedGroup = async (
    currentGroupData: WithOptionalId<Group>,
    newGroupData: WithOptionalId<Group>,
  ): Promise<boolean> => {
    const success = await handleEditGroup(currentGroupData, newGroupData);
    return success;
  };

  const handleOpenEditGroupDrillIn = (group: WithOptionalId<Group> | null) => {
    setEditedGroup(group);
    setIsEditGroupDrillInOpen(true);
  };

  const handleGroupSelect = (group: Group) => {
    setSelectedGroup(group.id);
    setSelectedGroupForDrillDown(group);
  };

  const handleGoToInDepthUserPage = (member: Member) => {
    setSelectedMemberForDrillDown(member);
  };

  const renderContent = () => {
    if (
      isEditGroupDrillInOpen ||
      isProgramsDrillInOpen ||
      selectedGroupForDrillDown
    )
      return null;
    return (
      <ListContent
        items={filteredGroups}
        listHeaderClassName={groupRowStyles.listGrid}
        EmptyStateComponent={renderEmptyState}
        titles={groupListTitles}
        ItemComponent={({ data }) => (
          <GroupRow
            group={data}
            deleteGroup={removeGroup}
            allowDelete={loginTrainer.id === data.trainerIds[0]}
            openEditGroupModal={() => handleOpenEditGroupDrillIn(data)}
            selected={selectedGroup === data.id}
            onSelect={() => handleGroupSelect(data)}
            key={data.id}
            trainers={Object.values(allGymTrainers)}
            onEditProgram={() => handleEditGroupProgram(data)}
            onCreateProgram={() => handleCreateGroupProgram(data)}
            onSubmitEditedGroup={handleSubmitEditedGroup}
          />
        )}
      />
    );
  };

  const handleSubmitEditedProgram = async (
    newProgramData: WithOptionalId<AssignedProgram>,
  ): Promise<boolean> => {
    let programResult: AssignedProgram | null = null;
    let success = false;

    if (!newProgramData.id) {
      const { success: createSuccess, program } = await createProgram(
        newProgramData,
        loginTrainer.id,
      );
      success = createSuccess;
      programResult = program as AssignedProgram | null;
    } else {
      success = await updateProgramAndRefreshPrograms(
        newProgramData as Program,
      );
      programResult = newProgramData as AssignedProgram;
    }

    if (!success || !programResult) {
      return false;
    }

    setEditedProgram({
      ...programResult,
      startDate: newProgramData.startDate ?? programResult.startDate,
    });
    setIsProgramsDrillInOpen(false);
    setIsSelectMembersModalOpen(true);
    return true;
  };

  const handleCloseProgramsDrillIn = () => {
    setIsProgramsDrillInOpen(false);
  };

  const handleCloseEditGroupDrillIn = () => {
    setIsEditGroupDrillInOpen(false);
  };

  const getDrillInContent = () => {
    if (editedProgram && isProgramsDrillInOpen) {
      return (
        <EditProgram
          program={editedProgram}
          onClose={() => handleCloseProgramsDrillIn()}
          onSubmit={handleSubmitEditedProgram}
          proceedAfterSave={false}
        />
      );
    } else if (editedGroup && isEditGroupDrillInOpen) {
      return (
        <EditGroup
          allGymTrainers={Object.values(allGymTrainers)}
          members={Object.values(allGymMembers)}
          onSubmit={(newGroupData) =>
            handleSubmitEditedGroup(editedGroup, newGroupData)
          }
          group={editedGroup}
          setGroup={
            setEditedGroup as React.Dispatch<
              React.SetStateAction<WithOptionalId<Group>>
            >
          }
          onClose={() => handleCloseEditGroupDrillIn()}
        />
      );
    } else if (selectedMemberForDrillDown) {
      return <InDepthMember member={selectedMemberForDrillDown} />;
    } else if (selectedGroupForDrillDown) {
      return (
        <GroupMembersView
          group={selectedGroupForDrillDown}
          allGymMembers={Object.values(allGymMembers)}
          allGymTrainers={Object.values(allGymTrainers)}
          userType={userType}
          onGoToInDepthUserPage={handleGoToInDepthUserPage}
        />
      );
    }

    return null;
  };

  const handleHeaderBack = () => {
    if (selectedMemberForDrillDown) {
      setSelectedMemberForDrillDown(null);
    } else if (selectedGroupForDrillDown) {
      setSelectedGroupForDrillDown(null);
    } else {
      setEditedProgram(null);
      setIsProgramsDrillInOpen(false);
      setEditedGroup(null);
      setIsEditGroupDrillInOpen(false);
    }
  };

  const getSearchPlaceholder = () => {
    if (!filteredGroups.length) return '';
    return 'Search for Groups';
  };

  const getTitle = () => {
    if (selectedMemberForDrillDown) {
      return getFullName(selectedMemberForDrillDown);
    } else if (selectedGroupForDrillDown) {
      return selectedGroupForDrillDown.name;
    } else if (editedGroup) {
      return `Edit Group ${editedGroup.name}`;
    }
    return 'Group';
  };

  const isDrilledIn = Boolean(
    isProgramsDrillInOpen ||
      isEditGroupDrillInOpen ||
      selectedMemberForDrillDown ||
      selectedGroupForDrillDown,
  );

  return (
    <ContentDrill
      title={getTitle()}
      onBack={isDrilledIn ? () => handleHeaderBack() : undefined}
      isParent
      actions={[
        {
          icon: <AddBoxOutlinedIcon />,
          label: 'Add A Group',
          onClick: () =>
            handleOpenEditGroupDrillIn(getEmptyGroup(loginTrainer.id)),
        },
      ]}
      searchPlaceholder={getSearchPlaceholder()}
      setSearchQuery={setSearchQuery}
      disableSearch={false}
      isDrilledIn={isDrilledIn}
      drillInContent={getDrillInContent()}
    >
      {renderContent()}
      {renderSelectMembersDialog()}
    </ContentDrill>
  );
};

export default Groups;
